import React, { useState, useEffect } from "react";
import { galleryAPI } from "../services/api";
import { useNavigate } from "react-router-dom";

const Gallery = () => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState("Tous");
  const [selectedImage, setSelectedImage] = useState(null);
  const navigate = useNavigate();

  const services = [
    "Tous",
    "Jardinage",
    "Bricolage",
    "Déménagement",
    "Nettoyage",
  ];

  useEffect(() => {
    loadGalleries();
  }, []);

  const loadGalleries = async () => {
    try {
      const response = await galleryAPI.getAll();
      if (response.success) {
        setGalleries(response.data);
      }
    } catch (error) {
      console.error("Erreur chargement galerie:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredGalleries =
    selectedService === "Tous"
      ? galleries
      : galleries.filter((item) => item.service === selectedService);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
  const BASE_URL = API_URL.replace("/api", "");

  return (
    <section id="galerie" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold mb-4">
            Nos <span className="gradient-text">Réalisations</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-6">
            Découvrez nos interventions et les résultats obtenus
          </p>
          <button
            onClick={() => navigate("/realisations")}
            className="bg-gradient-to-r from-orange-500 to-yellow-400 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg transition-all inline-flex items-center space-x-2"
          >
            <span>Voir toutes les réalisations</span>
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </button>
        </div>

        {/* Filtres */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {services.map((service) => (
            <button
              key={service}
              onClick={() => setSelectedService(service)}
              className={`px-6 py-3 rounded-full font-semibold transition-all ${
                selectedService === service
                  ? "bg-gradient-to-r from-orange-500 to-yellow-400 text-white shadow-lg"
                  : "bg-white text-gray-700 hover:bg-gray-100 border-2 border-gray-200"
              }`}
            >
              {service}
            </button>
          ))}
        </div>

        {/* Galerie */}
        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
            <p className="text-gray-600 mt-4">Chargement...</p>
          </div>
        ) : filteredGalleries.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-600 text-xl">
              Aucune réalisation pour le moment
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredGalleries.map((item) => (
              <div
                key={item._id}
                className="bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all cursor-pointer"
                onClick={() => setSelectedImage(item)}
              >
                {/* Images Avant/Après */}
                <div className="relative group">
                  <div className="grid grid-cols-2">
                    <div className="relative">
                      <img
                        src={BASE_URL + item.beforeImage}
                        alt="Avant"
                        className="w-full h-48 object-cover"
                      />
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        AVANT
                      </div>
                    </div>
                    <div className="relative">
                      <img
                        src={BASE_URL + item.afterImage}
                        alt="Après"
                        className="w-full h-48 object-cover"
                      />
                      <div className="absolute top-2 right-2 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        APRÈS
                      </div>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                    <span className="text-white font-bold text-lg opacity-0 group-hover:opacity-100 transition-opacity">
                      Voir les détails
                    </span>
                  </div>
                </div>

                {/* Infos */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="bg-orange-100 text-orange-600 px-3 py-1 rounded-full text-sm font-semibold">
                      {item.service}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {item.title}
                  </h3>
                  {item.description && (
                    <p className="text-gray-600 mb-3 line-clamp-2">
                      {item.description}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                    {item.location && (
                      <span className="flex items-center">
                        📍 {item.location}
                      </span>
                    )}
                    {item.duration && (
                      <span className="flex items-center">
                        ⏱️ {item.duration}
                      </span>
                    )}
                    {item.price && (
                      <span className="flex items-center font-semibold text-orange-600">
                        💰 {item.price}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal détails */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Images */}
            <div className="grid md:grid-cols-2 gap-1">
              <div className="relative">
                <img
                  src={BASE_URL + selectedImage.beforeImage}
                  alt="Avant"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-2 rounded-full font-bold">
                  AVANT
                </div>
              </div>
              <div className="relative">
                <img
                  src={BASE_URL + selectedImage.afterImage}
                  alt="Après"
                  className="w-full h-80 object-cover"
                />
                <div className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-full font-bold">
                  APRÈS
                </div>
              </div>
            </div>

            {/* Détails */}
            <div className="p-8">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-orange-100 text-orange-600 px-4 py-2 rounded-full font-semibold">
                  {selectedImage.service}
                </span>
                <button
                  onClick={() => setSelectedImage(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ✕
                </button>
              </div>

              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                {selectedImage.title}
              </h2>

              {selectedImage.description && (
                <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                  {selectedImage.description}
                </p>
              )}

              <div className="grid md:grid-cols-3 gap-4 mb-6">
                {selectedImage.location && (
                  <div className="bg-gray-50 p-4 rounded-xl">
                    <p className="text-gray-500 text-sm mb-1">Lieu</p>
                    <p className="font-semibold text-gray-800">
                      📍 {selectedImage.location}
                    </p>
                  </div>
                )}
                {selectedImage.duration && (
                  <div className="bg-gray-50 p-4 rounded-xl">
                    <p className="text-gray-500 text-sm mb-1">Durée</p>
                    <p className="font-semibold text-gray-800">
                      ⏱️ {selectedImage.duration}
                    </p>
                  </div>
                )}
                {selectedImage.price && (
                  <div className="bg-gray-50 p-4 rounded-xl">
                    <p className="text-gray-500 text-sm mb-1">Prix</p>
                    <p className="font-semibold text-orange-600">
                      💰 {selectedImage.price}
                    </p>
                  </div>
                )}
              </div>

              <button
                onClick={() => {
                  setSelectedImage(null);
                  const element = document.getElementById("contact");
                  if (element) {
                    element.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                className="w-full bg-gradient-to-r from-orange-500 to-yellow-400 text-white py-4 rounded-xl font-bold text-lg hover:shadow-lg transition-all"
              >
                Demander un devis similaire
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
